import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveLocale } from '@incpt/i18n';
import { ScreenBackdrop } from './ScreenBackdrop';
import { ChevronRight } from './icons';
import type { FaqItem, FaqText } from '../../lib/me';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';

function faqCopy(text: FaqText, lang: string): string {
  if (lang === 'uk') return text.uk || text.ru || text.en;
  if (lang === 'ru') return text.ru || text.en;
  const v = text[lang as keyof FaqText];
  return (typeof v === 'string' && v) || text.en;
}

export function FaqScreen({ items, onClose }: { items: FaqItem[]; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();
  const [open, setOpen] = useState<number | null>(null);
  const lang = resolveLocale(i18n.language);

  return (
    <div className="relative flex min-h-screen flex-col bg-surface">
      <ScreenBackdrop height={300} fadeStart={24} />
      <div className="relative z-10 flex min-h-screen flex-col px-4 pb-10 pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <header className="flex items-center gap-3 py-2">
          {!tgBack && (
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06]"
              aria-label="Back"
            >
              <ChevronRight size={16} className="rotate-180 text-ink" />
            </button>
          )}
          <h1 className="text-[20px] font-medium text-ink">{t('profile.faq')}</h1>
        </header>

        {items.length === 0 ? (
          <div className="mt-24 text-center text-[14px] text-muted">{t('profile.faqEmpty')}</div>
        ) : (
          <div className="mt-3 flex flex-col gap-2.5">
            {items.map((it, i) => {
              const isOpen = open === i;
              return (
                <div key={i} className="overflow-hidden rounded-[18px] bg-white shadow-card">
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="flex w-full items-center justify-between gap-3 p-4 text-left active:opacity-70"
                  >
                    <span className="text-[15px] font-medium text-ink">{faqCopy(it.q, lang)}</span>
                    <ChevronRight
                      size={18}
                      className={`shrink-0 text-muted transition-transform duration-300 ${
                        isOpen ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <p className="whitespace-pre-line px-4 pb-4 text-[14px] leading-relaxed text-muted">
                      {faqCopy(it.a, lang)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
